import React from 'react';
import * as d3 from 'd3';

import './Plot.css';

export interface DateValuePairs { value: number, time: number }
export interface AlgorithmPlotProps { selectFunction: (idx: number | null) => void, data: DateValuePairs[], selectedTime: number | null, title: string, xLabel: string, yLabel: string }

export class AlgorithmPlot extends React.Component<AlgorithmPlotProps, {}> {
    base: HTMLDivElement | null
    updateRef: (element: HTMLDivElement) => void

    constructor(props: AlgorithmPlotProps) {
        super(props)
        this.base = null

        this.updateRef = (element: HTMLDivElement) => {
            this.base = element;
            this.renderPlot();
        };
    }

    componentDidMount() {
        this.renderPlot();
    }

    componentDidUpdate(prevProps: AlgorithmPlotProps) {
        if (this.props.data !== prevProps.data) {
            this.renderPlot();
        }
    }

    selectTime(selection: number | null) {
        this.props.selectFunction(selection);
    }

    renderPlot() {
        if (this.base === null)
            return;
        const width = this.base!.clientWidth - 20;  // subtract 20 to compensate for margins
        const height = this.base!.clientHeight - 20;

        const plot_margin = { top: 10, right: 10, bottom: 40, left: 70 };
        const plot_width = width - plot_margin.left - plot_margin.right;
        const plot_height = height - plot_margin.top - plot_margin.bottom;

        const d3time = d3.timeParse('%Q')
        const time = (x: number) => d3time(Math.floor(x) as any)

        const xScale = d3.scaleTime()
            .domain((this.props.data.length === 0) ? [new Date(), new Date()] :
                [time(this.props.data[0].time)!, time(this.props.data[this.props.data.length - 1].time)!])
            .range([0, plot_width]);

        const yScale = d3.scaleLinear()
            .domain([d3.min(this.props.data.map((d) => d.value)) || 0,
            Math.max(d3.max(this.props.data.map((d) => d.value)) || 1, 3)])
            .range([plot_height, 0]);

        const line = d3.line<DateValuePairs>()
            .x(function (d) { return xScale(d.time) as number; })
            .y(function (d) { return yScale(d.value) as number; })

        d3.select(this.base!).select("svg").remove();
        const svg = d3.select(this.base!).append("svg")
            .attr("width", `${width}px`)
            .attr("height", `${height}px`);

        var callback = this.selectTime.bind(this);
        svg.on("mouseup", function () {
            var ypos = d3.mouse(this)[1];
            if (ypos < plot_margin.top || ypos > height - plot_margin.bottom)
                callback(null);
            else
                callback(xScale.invert(d3.mouse(this)[0] - plot_margin.left).getTime());
            console.log("selecting time", xScale.invert(d3.mouse(this)[0] - plot_margin.left));
        });

        const xAxis = svg.append('g')
            .attr('class', 'plot-axis bottom')
        xAxis.append('g')
            .attr('transform', `translate(${plot_margin.left},${plot_height + plot_margin.top})`)
            .call(d3.axisBottom(xScale));
        xAxis.append('g')
            .attr('class', 'label')
            .append("text")
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "text-after-edge")
            .attr("transform", `translate(${plot_margin.left + plot_width / 2},${height})`)
            .text(this.props.xLabel);

        const yAxis = svg.append('g')
            .attr('class', 'plot-axis left')
        yAxis.append('g')
            .attr('transform', `translate(${plot_margin.left},${plot_margin.top})`)
            .call(d3.axisLeft(yScale).ticks(5));
        yAxis.append('g')
            .attr('class', 'label')
            .append("text")
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "hanging")
            .attr("transform", `translate(0,${plot_margin.top + plot_height / 2})rotate(-90)`)
            .text(this.props.yLabel);

        const plot_area = svg.append("g")
            .attr("class", "plot-area")
            .attr('transform', `translate(${plot_margin.left},${plot_margin.top})`)

        plot_area.append("path")
            .datum(this.props.data)
            .attr("d", line);


        if (this.props.selectedTime !== null && this.props.data.length !== 0) {
            var selTime = this.props.selectedTime;
            if (selTime < d3.min(this.props.data.map((d) => d.time))!)
                selTime = d3.min(this.props.data.map((d) => d.time))!

            if(xScale(selTime) !== undefined)
            plot_area.append("line")
                .attr("x1", xScale(selTime) as number)
                .attr("y1", 0)
                .attr("x2", xScale(selTime) as number)
                .attr("y2", height - plot_margin.top - plot_margin.bottom)
                .style("stroke-width", 2)
                .style("stroke", "red")
                .style("fill", "none");
        }
    }

    render() {
        return (
            <div className="Plot">
                <div className="header">
                    <span>{this.props.title}</span>
                </div>
                <div className="plot" ref={this.updateRef} />
            </div>
        )
    }
}

export default AlgorithmPlot
